import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Search, AlertCircle, RefreshCw, CheckCircle, Info } from 'lucide-react'
import { ersatzteile as ersatzteileApi, bexio as bexioApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { formatWaehrung } from '../../lib/utils'

export function ErsatzteileListePage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [syncResult, setSyncResult] = useState<{ added: number; updated: number; synced: number } | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['ersatzteile'], queryFn: () => ersatzteileApi.list() })

  const syncMutation = useMutation({
    mutationFn: () => bexioApi.syncArtikel(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['ersatzteile'] })
      setSyncResult({ added: res.added, updated: res.updated, synced: res.synced })
      setTimeout(() => setSyncResult(null), 6000)
    },
  })

  const filtered = (data ?? []).filter((e) =>
    [e.bezeichnung, e.artikelnummer, e.fuer_modell].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
  )

  const unterbestand = filtered.filter((e) => e.lagerbestand <= e.mindestbestand)

  return (
    <>
      <TopBar
        title="Ersatzteile"
        rightAction={
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />}
            loading={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            Bexio Sync
          </Button>
        }
      />

      <div className="px-4 py-3 space-y-3 pb-8">
        {/* Bexio master hint */}
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
          <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700">
            Bexio ist die Datenquelle. Preise und Lagerbestände werden per Sync aktualisiert.
          </p>
        </div>

        {/* Sync success banner */}
        {syncResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
            <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-sm">Sync erfolgreich</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                {syncResult.synced} Artikel geprüft · {syncResult.added} neu · {syncResult.updated} aktualisiert
              </p>
            </div>
          </div>
        )}

        {/* Sync error */}
        {syncMutation.error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
            <p className="text-sm text-red-700">{(syncMutation.error as Error).message}</p>
          </div>
        )}

        {/* Low stock warning */}
        {unterbestand.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle size={16} className="text-amber-600" />
              <p className="font-semibold text-amber-900 text-sm">{unterbestand.length} Artikel unter Mindestbestand</p>
            </div>
            {unterbestand.slice(0, 3).map((e) => (
              <p key={e.id} className="text-xs text-amber-700">
                · {e.bezeichnung}: {e.lagerbestand} {e.einheit} (Min: {e.mindestbestand})
              </p>
            ))}
            {unterbestand.length > 3 && (
              <p className="text-xs text-amber-600 mt-1">+ {unterbestand.length - 3} weitere</p>
            )}
          </div>
        )}

        <Input
          placeholder="Suche nach Bezeichnung, Artikelnr. ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search size={16} />}
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Package size={28} />}
            title="Keine Ersatzteile"
            description="Bexio Sync starten um Artikel zu importieren"
            action={
              <Button
                icon={<RefreshCw size={16} />}
                loading={syncMutation.isPending}
                onClick={() => syncMutation.mutate()}
              >
                Jetzt synchronisieren
              </Button>
            }
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((e) => {
              const unterMindest = e.lagerbestand <= e.mindestbestand
              return (
                <Card key={e.id} className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${unterMindest ? 'bg-amber-50' : 'bg-gray-100'}`}>
                    <Package size={18} className={unterMindest ? 'text-amber-600' : 'text-gray-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm text-gray-900 truncate">{e.bezeichnung}</p>
                      {e.bexio_id && (
                        <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-medium shrink-0">
                          Bexio
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {e.artikelnummer && <span className="text-xs text-gray-400">{e.artikelnummer}</span>}
                      <Badge variant={e.fuer_modell === 'alle' ? 'gray' : 'blue'}>{e.fuer_modell}</Badge>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-bold text-sm ${unterMindest ? 'text-amber-600' : 'text-gray-900'}`}>
                      {e.lagerbestand} {e.einheit}
                    </p>
                    {e.preis ? <p className="text-xs text-gray-500">{formatWaehrung(e.preis)}</p> : null}
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
